import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { API_URL } from "../global_constants";
import { authService } from '../services/AuthServices';
import iconimg from "../assets/header_logo.png";
import welcomeImg from "../assets/welcome.jpg";
import { toast } from 'react-toastify';

const Signup = () => {
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1);

  const [otpFieldsVisible, setOtpFieldsVisible] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [isSendOtpDisabled, setIsSendOtpDisabled] = useState(false);
  const [otpSentOnce, setOtpSentOnce] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Refs for OTP inputs
  const otpRefs = useRef([]);

  useEffect(() => {
    if (timer > 0 && isSendOtpDisabled) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    if (timer === 0) {
      setIsSendOtpDisabled(false);
    }
  }, [timer, isSendOtpDisabled]);

  const saveTempUser = async () => {
    const data = { fullName, email };
    await authService.saveTempUser(data);
  };

  const sendOtp = async () => {
    try {
      if (!fullName || !email) {
        toast.error("Full name and email are required to send OTP.");
        return;
      }

      if (!otpSentOnce) {
        await saveTempUser();
        setOtpSentOnce(true);
      }

      const otpData = { email };
      await authService.sendOtp(otpData);

      toast.success("OTP has been sent to your email.");
      setOtpFieldsVisible(true);
      setIsSendOtpDisabled(true);
      setTimer(30);
    } catch (err) {
      console.error("OTP send error", err);
      alert("Failed to send OTP. Please try again.");
    }
  };

  const verifyOtp = async () => {
    try {
      const enteredOtp = otp.join('');
      const verifyData = { email, otp: enteredOtp };
      await authService.verifyOtp(verifyData);

      toast.success("OTP verified successfully.");
      setOtpVerified(true);
      setNextEnabled(true);
      setOtpFieldsVisible(false);
    } catch (err) {
      toast.error("Invalid OTP.");
    }
  };

  const handleOtpChange = (index, value) => {
    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(-1);
    setOtp(updatedOtp);

    // Auto-focus next input
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace to go to previous field
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const resendOtp = () => {
    if (!isSendOtpDisabled) {
      sendOtp();
    } else {
      toast.warning("Please wait for the timer to finish before resending OTP.");
    }
  };

  const handleNext = () => {
    if (step === 1 && !fullName) return toast.error("Full Name is required");
    if (step === 2 && !email) return toast.error("Email is required");
    if (step === 3 && (password !== confirmPassword)) return toast.error("Passwords do not match");

    setStep(prev => prev + 1);
  };

  const handleBack = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError('Passwords do not match.');
    }

    try {
      const data = { fullName, email, phoneNumber, password, referralCode };
      await authService.register(data);

      setSuccess('You registered successfully. Please login now.');
      setTimeout(() => {
        navigate('/user/login');
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'There was an error. Please try again.';
      setError(errorMessage);
    }
  };

  return (
    <div id="cryptoon-layout" className="theme-orange">
      <style jsx>{`
        * {
          box-sizing: border-box;
        }
        
        .auth-container {
          min-height: 100vh;
          background: #ffffff;
          overflow-x: hidden;
        }
        
        .auth-card {
          border-radius: 24px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          background: #ffffff;
          transition: all 0.3s ease;
        }
        
        .auth-card:hover {
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .step-indicator {
          display: flex;
          justify-content: center;
          margin-bottom: 1.5rem;
          position: relative;
        }
        
        .step-indicator::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 20%;
          right: 20%;
          height: 2px;
          background: #e9ecef;
          z-index: 1;
        }
        
        .step-dot {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e9ecef;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.4s ease;
          position: relative;
          z-index: 2;
          cursor: pointer;
        }
        
        .step-dot:hover {
          transform: scale(1.1);
        }
        
        .step-dot.active {
          background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
          color: #212529;
          transform: scale(1.15);
          box-shadow: 0 8px 25px rgba(255, 193, 7, 0.3);
        }
        
        .step-dot.completed {
          background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
        }
        
        .form-control {
          border-radius: 16px;
          border: 2px solid #e9ecef;
          padding: 16px 20px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #fafafa;
        }
        
        .form-control:hover {
          border-color: #ffc107;
          background: #ffffff;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 193, 7, 0.1);
        }
        
        .form-control:focus {
          border-color: #ffc107;
          box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
          transform: translateY(-2px);
          background: #ffffff;
          outline: none;
        }
        
        .form-label {
          font-weight: 600;
          color: #ffc107;
          margin-bottom: 8px;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .btn {
          border-radius: 16px;
          padding: 14px 28px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          transition: all 0.3s ease;
          border: none;
          position: relative;
          overflow: hidden;
        }
        
        .btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }
        
        .btn:hover::before {
          left: 100%;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
          color: #212529;
          box-shadow: 0 8px 25px rgba(255, 193, 7, 0.3);
        }
        
        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(255, 193, 7, 0.4);
          background: linear-gradient(135deg, #ff8f00 0%, #e65100 100%);
          color: #212529;
        }
        
        .btn-secondary {
          background: linear-gradient(135deg, #6c757d 0%, #545b62 100%);
          color: white;
          box-shadow: 0 8px 25px rgba(108, 117, 125, 0.2);
        }
        
        .btn-secondary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(108, 117, 125, 0.3);
          background: linear-gradient(135deg, #545b62 0%, #3d4147 100%);
        }
        
        .otp-container {
          background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
          border-radius: 20px;
          padding: 30px;
          margin: 24px 0;
          border: 2px solid #ffc107;
          position: relative;
          overflow: hidden;
        }
        
        .otp-container::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 193, 7, 0.05) 0%, transparent 70%);
          animation: pulse 3s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        .otp-input {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          border: 3px solid #ffc107;
          text-align: center;
          font-size: 24px;
          font-weight: bold;
          background: white;
          transition: all 0.3s ease;
          margin: 0 8px;
          position: relative;
          z-index: 2;
          color: #212529;
        }
        
        .otp-input:hover {
          border-color: #ff8f00;
          transform: scale(1.05);
          box-shadow: 0 8px 25px rgba(255, 193, 7, 0.2);
        }
        
        .otp-input:focus {
          border-color: #ff8f00;
          box-shadow: 0 0 0 0.3rem rgba(255, 193, 7, 0.25);
          transform: scale(1.1);
          outline: none;
        }
        
        .welcome-image {
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          transition: all 0.5s ease;
          max-height: 500px;
          object-fit: cover;
        }
        
        .welcome-image:hover {
          transform: scale(1.02) rotate(1deg);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
        }
        
        .logo-container {
          padding: 15px 0;
          margin-bottom: 15px;
        }
        
        .logo-container img {
          transition: all 0.3s ease;
        }
        
        .logo-container img:hover {
          transform: scale(1.05);
          filter: drop-shadow(0 8px 16px rgba(255, 193, 7, 0.3));
        }
        
        .main-title {
          background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
          margin-bottom: 8px;
          font-size: 1.8rem;
        }
        
        .main-subtitle {
          color: #6c757d;
          font-size: 0.95rem;
          margin-bottom: 1.5rem;
        }
        
        .alert-success {
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          border: 2px solid #28a745;
          border-radius: 16px;
          color: #155724;
          animation: slideInFromTop 0.5s ease;
        }
        
        .alert-danger {
          background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
          border: 2px solid #dc3545;
          border-radius: 16px;
          color: #721c24;
          animation: slideInFromTop 0.5s ease;
        }
        
        .verification-success {
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          border-radius: 20px;
          padding: 24px;
          text-align: center;
          margin: 24px 0;
          border: 3px solid #28a745;
          position: relative;
          overflow: hidden;
          animation: bounceIn 0.6s ease;
        }
        
        .verification-success::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(40, 167, 69, 0.1) 0%, transparent 70%);
          animation: rotate 3s linear infinite;
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes slideInFromTop {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease forwards;
        }
        
        .timer-text {
          color: #6c757d;
          font-size: 14px;
          margin-top: 12px;
          font-weight: 500;
        }
        
        .form-step {
          min-height: 350px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        
        .image-container {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 500px;
        }
        
        .btn-link {
          color: #ffc107 !important;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          padding: 8px 16px;
          border-radius: 12px;
          position: relative;
        }
        
        .btn-link:hover {
          background: rgba(255, 193, 7, 0.1);
          transform: translateY(-1px);
          color: #ff8f00 !important;
        }
        
        .signin-link {
          color: #ffc107 !important;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          padding: 4px 8px;
          border-radius: 8px;
          position: relative;
          display: inline-block;
        }
        
        .signin-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: 0;
          left: 50%;
          background-color: #ffc107;
          transition: all 0.3s ease;
        }
        
        .signin-link:hover {
          color: #ff8f00 !important;
          transform: translateY(-1px);
        }
        
        .signin-link:hover::after {
          width: 100%;
          left: 0;
        }
        
        .form-container {
          max-width: 600px;
          margin: 0 auto;
        }
        
        .step-title {
          color: #2c3e50;
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          text-align: center;
        }
        
        @media (max-width: 768px) {
          .main-title { font-size: 1.5rem; }
          .main-subtitle { font-size: 0.9rem; }
          .form-container { max-width: 100%; }
          .image-container { min-height: 300px; }
          .otp-input { width: 50px; height: 50px; margin: 0 4px; }
          .form-step { min-height: 300px; }
        }
      `}</style>
      
      <div className="auth-container">
        <div className="container-fluid px-4">
          {/* Logo Header */}
          <div className="row">
            <div className="col-12">
              <div className="logo-container text-center text-lg-start">
                <Link to="/">
                  <img src={iconimg} width={120} alt="Logo" />
                </Link>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="row align-items-center min-vh-100 py-4">
            {/* Form Section */}
            <div className="col-lg-7 col-xl-6">
              <div className="form-container">
                <div className="text-center text-lg-start mb-3">
                  <h2 className="main-title">Create Account</h2>
                  <p className="main-subtitle">Join us today and get started</p>
                </div>
                
                {/* Step Indicator */}
                <div className="step-indicator mb-4">
                  <div className={`step-dot ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                    {step > 1 ? '✓' : '1'}
                  </div>
                  <div className={`step-dot ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                    {step > 2 ? '✓' : '2'}
                  </div>
                  <div className={`step-dot ${step >= 3 ? 'active' : ''}`}>3</div>
                </div>
                
                <div className="auth-card">
                  <div className="card-body p-4 p-lg-5">
                    <form onSubmit={handleSubmit}>
                      <div className="form-step">
                        {step === 1 && (
                          <div className="fade-in-up">
                            <h4 className="step-title">Personal Information</h4>
                            <div className="mb-4">
                              <label className="form-label">Full Name *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Enter your full name"
                                required
                              />
                            </div>
                            <div className="mb-4">
                              <label className="form-label">Phone Number</label>
                              <input
                                type="text"
                                className="form-control"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="Enter your phone number (optional)"
                              />
                              <small className="text-muted">Optional - for account recovery</small>
                            </div>
                            <div className="mb-4">
                              <label className="form-label">Referral Code</label>
                              <input
                                type="text"
                                className="form-control"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                                placeholder="Enter referral code (optional)"
                              />
                              <small className="text-muted">Optional - if you were referred by someone</small>
                            </div>
                          </div>
                        )}

                        {step === 2 && (
                          <div className="fade-in-up">
                            <h4 className="step-title">Email Verification</h4>
                            <div className="mb-4">
                              <label className="form-label">Email Address *</label>
                              <input
                                type="email"
                                className="form-control"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                required
                              />
                            </div>

                            {!otpFieldsVisible && !otpVerified && (
                              <div className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-primary px-5"
                                  onClick={sendOtp}
                                >
                                  Send Verification Code
                                </button>
                              </div>
                            )}

                            {otpFieldsVisible && !otpVerified && (
                              <div className="otp-container">
                                <h6 className="text-center mb-4 fw-bold" style={{color: '#2c3e50'}}>
                                  Enter 4-digit code sent to your email
                                </h6>
                                <div className="d-flex justify-content-center mb-4">
                                  {otp.map((val, idx) => (
                                    <input
                                      key={idx}
                                      ref={(el) => otpRefs.current[idx] = el}
                                      type="text"
                                      className="otp-input"
                                      placeholder="0"
                                      autoComplete="off"
                                      maxLength={1}
                                      value={val}
                                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                    />
                                  ))}
                                </div>
                                <div className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-primary px-5"
                                    onClick={verifyOtp}
                                  >
                                    Verify Account
                                  </button>
                                </div>
                              </div>
                            )}

                            {otpVerified && (
                              <div className="verification-success">
                                <div className="fa-3x text-success mb-3" style={{position: 'relative', zIndex: 2, fontSize: '3rem'}}>✓</div>
                                <h5 className="text-success mb-0 fw-bold" style={{position: 'relative', zIndex: 2}}>Email Verified Successfully!</h5>
                              </div>
                            )}

                            {!otpVerified && otpSentOnce && (
                              <div className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-link"
                                  onClick={resendOtp}
                                  disabled={isSendOtpDisabled}
                                >
                                  {isSendOtpDisabled
                                    ? `Resend Code in ${timer}s`
                                    : 'Resend Verification Code'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {step === 3 && (
                          <div className="fade-in-up">
                            <h4 className="step-title">Create Password</h4>
                            <div className="mb-4">
                              <label className="form-label">Password *</label>
                              <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Create a strong password"
                                required
                              />
                              <small className="text-muted">Use 8+ characters with letters, numbers & symbols</small>
                            </div>
                            <div className="mb-4">
                              <label className="form-label">Confirm Password *</label>
                              <input
                                type="password"
                                className="form-control"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your password"
                                required
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Navigation Buttons */}
                      <div className="d-flex justify-content-between align-items-center mt-5 pt-3">
                        {step > 1 && (
                          <button type="button" className="btn btn-secondary px-4" onClick={handleBack}>
                            Back
                          </button>
                        )}
                        
                        {step < 3 && step !== 2 && (
                          <button type="button" className="btn btn-primary ms-auto px-5" onClick={handleNext}>
                            Next
                          </button>
                        )}
                        
                        {step === 2 && otpVerified && (
                          <button type="button" className="btn btn-primary ms-auto px-5" onClick={handleNext}>
                            Next
                          </button>
                        )}
                        
                        {step === 3 && (
                          <button type="submit" className="btn btn-primary ms-auto px-5">
                            Create Account
                          </button>
                        )}
                      </div>

                      {/* Alerts */}
                      {error && (
                        <div className="alert alert-danger mt-4 mb-0" role="alert">
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="alert alert-success mt-4 mb-0" role="alert">
                          {success}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
                
                {/* Login Link */}
                <div className="text-center mt-4">
                  <p className="mb-0 text-muted">
                    Already have an account? 
                    <Link to="/user/login" className="signin-link ms-2">
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Image Section */}
            <div className="col-lg-5 col-xl-6">
              <div className="image-container">
                <img src={welcomeImg} alt="Welcome" className="img-fluid welcome-image" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;